import type { FC } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import Typography from "@mui/material/Typography";
import { useAuth } from "react-oidc-context";
import { Strong } from "../../../shared/Strong";
import { useForm } from "react-hook-form";
import * as FormDialog from "../../../shared/FormDialog";
import type { UseMutationResult } from "@tanstack/react-query";
import type { RegistrationById, StatusChangeRequest as StatusChangePayload } from "../../../api/registrations.types";

type FormValues = {
  additionalText: string;
};

type RegistrationRequestCompletionDialogProps = {
  onClose: () => void;
  updateStatusMutation: UseMutationResult<unknown, Error, StatusChangePayload>;
  registration: RegistrationById;
};

export const RegistrationRequestCompletionDialog: FC<RegistrationRequestCompletionDialogProps> = ({
  onClose,
  updateStatusMutation,
  registration,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const status = registration.status.status;
  const responsible = registration.responsible;
  const recipient =
    responsible && responsible.userId !== user?.profile.sub
      ? `${responsible.firstName} ${responsible.lastName}`.trim() || responsible.email
      : undefined;
  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: { additionalText: "" },
  });

  const handleConfirm = ({ additionalText }: FormValues) => {
    const statusIsAudit = status === "audit";
    const payload: StatusChangePayload = statusIsAudit
      ? { status: "audit", auditStatus: "completion", additionalText }
      : { status: "completion", additionalText };

    return updateStatusMutation.mutateAsync(payload, { onSuccess: onClose });
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("RegistrationDrawer.RequestCompletion.DialogHeader")}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(handleConfirm)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={updateStatusMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="additionalText"
              rules={{ required: t("Input.Validation.Required") }}
              label={t("RegistrationDrawer.RequestCompletion.Label")}
              multiline
              rows={7}
            />
          </FormDialog.Fields>
          {recipient && (
            <Typography sx={{ mt: 1, fontSize: theme => theme.typography.pxToRem(13), color: "text.secondary" }}>
              <Translate
                translationKey="RegistrationDrawer.RequestCompletion.WhatHappens"
                values={{ recipient: <Strong>{recipient}</Strong> }}
              />
            </Typography>
          )}
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={updateStatusMutation.isPending}>
            {t("RegistrationDrawer.RequestCompletion.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
